import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ServiceLayout } from '@/components/ServiceLayout';
import {
  Plus, Pencil, Trash2, Save, X, Loader2, MessageCircle, Mail,
  Eye, ToggleLeft, ToggleRight, Info,
} from 'lucide-react';
import { useServiceMasters } from '@/hooks/useServiceMasters';
import { usePermissions, RESOURCES } from '@/contexts/PermissionsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLACEHOLDERS = [
  'job_no', 'party_name', 'model', 'variant', 'brand', 'reg_no', 'chassis_no',
  'status', 'vehicle_in_date', 'expected_delivery_date', 'technician',
  'supervisor', 'service_head', 'mobile_no_1', 'email', 'company_name',
  // iter-121: Additional Customer Details placeholders (Address / Insurance / DMS).
  // Resolved from the job document at send time; falls back to the linked
  // booking record when the job's value is blank.
  'building_name', 'door_no', 'address_1', 'address_2', 'address_3',
  'area', 'city', 'state', 'pin_code',
  'insurance_company_name', 'policy_no', 'own_damage_expiry_date',
  'dms_invoice_no', 'dms_invoice_date',
  // iter-122: Reconciliation outstanding amount — for collection reminders &
  // refund/credit notices fired from the AI Assistant Reconciliation table.
  'outstanding_amount',
];

const inputCx = 'w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-[14px] text-slate-900 placeholder-slate-400 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all';

export default function ServiceTemplates({ user, onLogout }) {
  const [templates, setTemplates] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null);
  const [previewBody, setPreviewBody] = React.useState({ open: false, html: '', subject: '' });
  const bodyRef = React.useRef(null);
  const masters = useServiceMasters();
  const { canDelete } = usePermissions();
  const canDeleteService = canDelete(RESOURCES.SERVICE);

  const fetchTemplates = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/service/templates`);
      setTemplates(data || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openCreate = (channel) => setEditing({
    _new: true, channel, trigger: (masters.status && masters.status[0]) || '', name: '', subject: '', body: '',
    enabled: true, recipients: ['supervisor', 'technician'],
  });
  const openEdit = (tpl) => setEditing({ ...tpl });
  const closeForm = () => setEditing(null);

  const handleSave = async () => {
    if (!editing.name?.trim()) { toast.error('Name is required'); return; }
    if (!editing.body?.trim()) { toast.error('Body is required'); return; }
    if (!editing.trigger) { toast.error('Please select a Trigger Status'); return; }
    if (!Array.isArray(editing.recipients) || editing.recipients.length === 0) {
      toast.error('Please select at least one recipient (Supervisor / Technician / Customer)');
      return;
    }
    try {
      const payload = { ...editing };
      delete payload._new; delete payload.id; delete payload.created_at;
      if (editing._new) {
        await axios.post(`${API}/service/templates`, payload);
        toast.success('Template created');
      } else {
        await axios.put(`${API}/service/templates/${editing.id}`, payload);
        toast.success('Template updated');
      }
      closeForm();
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${API}/service/templates/${tpl.id}`);
      toast.success('Deleted');
      fetchTemplates();
    } catch {
      toast.error('Delete failed');
    }
  };

  const toggleEnabled = async (tpl) => {
    try {
      await axios.put(`${API}/service/templates/${tpl.id}`, { ...tpl, enabled: !tpl.enabled });
      fetchTemplates();
    } catch {
      toast.error('Update failed');
    }
  };

  const handlePreview = async (tpl) => {
    try {
      const { data } = await axios.post(`${API}/service/templates/preview`, { template_id: tpl.id });
      setPreviewBody({ open: true, html: data.body || '', subject: data.subject || '' });
    } catch {
      toast.error('Preview failed');
    }
  };

  const insertPlaceholder = (ph) => {
    if (!bodyRef.current) return;
    const el = bodyRef.current;
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const value = editing.body || '';
    const next = value.slice(0, start) + `{{${ph}}}` + value.slice(end);
    setEditing((e) => ({ ...e, body: next }));
    // Restore caret after insert
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + ph.length + 4;
      el.setSelectionRange(pos, pos);
    });
  };

  const grouped = React.useMemo(() => {
    const wa = templates.filter((t) => t.channel === 'whatsapp');
    const em = templates.filter((t) => t.channel === 'email');
    return { whatsapp: wa, email: em };
  }, [templates]);

  return (
    <ServiceLayout user={user} onLogout={onLogout} title="Notification Templates" subtitle="Customize the WhatsApp & Email messages sent to customers when a job's status changes.">
      <div className="space-y-5" data-testid="service-templates-page">
        {/* Info banner */}
        <div className="rounded-2xl bg-gradient-to-r from-sky-50 to-indigo-50 border border-sky-200 p-4 flex items-start gap-3">
          <Info size={18} className="text-sky-600 shrink-0 mt-0.5" />
          <div className="text-[13px] text-slate-700">
            <p className="font-medium text-slate-900 mb-0.5">How notifications work</p>
            <p>Templates are matched to a job's status when you save a Job Card. When the status changes (e.g. → Invoice), you'll be prompted to send a WhatsApp / Email message using these templates. Use placeholders like <code className="px-1.5 py-0.5 bg-white border border-sky-200 rounded text-[11.5px]">{'{{party_name}}'}</code> and <code className="px-1.5 py-0.5 bg-white border border-sky-200 rounded text-[11.5px]">{'{{job_no}}'}</code> — they're replaced automatically.</p>
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 p-12 text-center">
            <Loader2 size={22} className="animate-spin text-sky-500 mx-auto" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* WhatsApp column */}
            <div className="rounded-2xl bg-white/75 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] overflow-hidden">
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 border-b border-emerald-200/50">
                <div className="w-9 h-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow-md">
                  <MessageCircle size={17} />
                </div>
                <div className="flex-1">
                  <h2 className="text-[14px] font-semibold text-slate-900">WhatsApp Templates</h2>
                  <p className="text-[11.5px] text-slate-500">{grouped.whatsapp.length} configured</p>
                </div>
                <button
                  onClick={() => openCreate('whatsapp')}
                  data-testid="add-whatsapp-template"
                  className="px-3 py-1.5 rounded-xl bg-emerald-600 text-white text-[12.5px] font-medium hover:bg-emerald-700 inline-flex items-center gap-1.5"
                >
                  <Plus size={13} /> Add
                </button>
              </div>
              <ul className="divide-y divide-slate-100">
                {grouped.whatsapp.length === 0 ? (
                  <li className="p-8 text-center text-slate-400 text-[13px]">No WhatsApp templates yet</li>
                ) : grouped.whatsapp.map((tpl) => (
                  <TemplateRow key={tpl.id} tpl={tpl} onEdit={openEdit} onDelete={handleDelete} onToggle={toggleEnabled} onPreview={handlePreview} />
                ))}
              </ul>
            </div>

            {/* Email column */}
            <div className="rounded-2xl bg-white/75 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] overflow-hidden">
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-sky-500/10 to-indigo-500/5 border-b border-sky-200/50">
                <div className="w-9 h-9 rounded-xl bg-sky-600 text-white flex items-center justify-center shadow-md">
                  <Mail size={17} />
                </div>
                <div className="flex-1">
                  <h2 className="text-[14px] font-semibold text-slate-900">Email Templates</h2>
                  <p className="text-[11.5px] text-slate-500">{grouped.email.length} configured</p>
                </div>
                <button
                  onClick={() => openCreate('email')}
                  data-testid="add-email-template"
                  className="px-3 py-1.5 rounded-xl bg-sky-600 text-white text-[12.5px] font-medium hover:bg-sky-700 inline-flex items-center gap-1.5"
                >
                  <Plus size={13} /> Add
                </button>
              </div>
              <ul className="divide-y divide-slate-100">
                {grouped.email.length === 0 ? (
                  <li className="p-8 text-center text-slate-400 text-[13px]">No Email templates yet</li>
                ) : grouped.email.map((tpl) => (
                  <TemplateRow key={tpl.id} tpl={tpl} onEdit={openEdit} onDelete={handleDelete} onToggle={toggleEnabled} onPreview={handlePreview} />
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={closeForm} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-white/50 w-full max-w-3xl p-6 max-h-[92vh] overflow-y-auto" data-testid="template-editor-modal">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-[16px] font-semibold text-slate-900">{editing._new ? 'New Template' : 'Edit Template'}</h3>
                <p className="text-[12px] text-slate-500">
                  {editing.channel === 'whatsapp' ? 'WhatsApp message — keep it concise and friendly.' : 'Email message — supports basic HTML for formatting.'}
                </p>
              </div>
              <button onClick={closeForm} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={16} /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">Name *</label>
                <input value={editing.name || ''} onChange={(e) => setEditing((x) => ({ ...x, name: e.target.value }))} className={inputCx} data-testid="tpl-name" />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">Trigger Status *</label>
                <select value={editing.trigger || ''} onChange={(e) => setEditing((x) => ({ ...x, trigger: e.target.value }))} className={inputCx} data-testid="tpl-trigger">
                  <option value="" disabled>{masters.loading ? 'Loading status master…' : 'Select a status'}</option>
                  {(masters.status || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-[12px] font-medium text-slate-600 mb-1.5">Send To *</label>
                <div className="flex flex-wrap items-center gap-3 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
                  {[
                    { id: 'supervisor', label: 'Supervisor', hint: 'Sent to assigned supervisor (master mobile/email)' },
                    { id: 'technician', label: 'Technician', hint: 'Sent to assigned technician (master mobile/email)' },
                    { id: 'customer',   label: 'Customer',   hint: 'Sent to job customer (booking mobile/email)' },
                  ].map((r) => {
                    const isOn = (editing.recipients || []).includes(r.id);
                    return (
                      <label key={r.id} title={r.hint} className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isOn}
                          onChange={(e) => {
                            const next = new Set(editing.recipients || []);
                            if (e.target.checked) next.add(r.id); else next.delete(r.id);
                            setEditing((x) => ({ ...x, recipients: [...next] }));
                          }}
                          data-testid={`tpl-recipient-${r.id}`}
                          className="w-4 h-4 rounded border-slate-300 text-sky-600"
                        />
                        <span className={`text-[13px] ${isOn ? 'text-sky-700 font-medium' : 'text-slate-700'}`}>{r.label}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-1">
                  <Info size={11} /> Pick one or more recipients. Default: Supervisor + Technician (Customer must be explicitly chosen).
                </p>
              </div>
              {editing.channel === 'email' && (
                <div className="md:col-span-2">
                  <label className="block text-[12px] font-medium text-slate-600 mb-1">Subject</label>
                  <input value={editing.subject || ''} onChange={(e) => setEditing((x) => ({ ...x, subject: e.target.value }))} className={inputCx} placeholder="e.g. Your service is ready — Job #{{job_no}}" data-testid="tpl-subject" />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="block text-[12px] font-medium text-slate-600 mb-1">Body *</label>
                <textarea
                  ref={bodyRef}
                  value={editing.body || ''}
                  onChange={(e) => setEditing((x) => ({ ...x, body: e.target.value }))}
                  rows={editing.channel === 'email' ? 8 : 5}
                  className={`${inputCx} font-mono text-[13px] leading-relaxed`}
                  placeholder={editing.channel === 'whatsapp' ? 'Hi {{party_name}}, your job #{{job_no}} is ready…' : '<p>Dear {{party_name}}, …</p>'}
                  data-testid="tpl-body"
                />
                <p className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-1">
                  <Info size={11} /> Click a placeholder below to insert it at the cursor position.
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11.5px] font-medium text-slate-600 mb-1.5">Available placeholders</label>
                <div className="flex flex-wrap gap-1.5">
                  {PLACEHOLDERS.map((ph) => (
                    <button
                      key={ph}
                      type="button"
                      onClick={() => insertPlaceholder(ph)}
                      data-testid={`placeholder-${ph}`}
                      className="px-2 py-1 rounded-md bg-sky-50 text-sky-700 border border-sky-200 text-[11.5px] hover:bg-sky-100 font-mono"
                    >
                      {`{{${ph}}}`}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2 flex items-center gap-3">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editing.enabled}
                    onChange={(e) => setEditing((x) => ({ ...x, enabled: e.target.checked }))}
                    data-testid="tpl-enabled"
                    className="w-4 h-4 rounded border-slate-300 text-sky-600"
                  />
                  <span className="text-[13px] text-slate-700">Enabled (offer to send when this status fires)</span>
                </label>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={closeForm} className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 text-[13px]">Cancel</button>
              <button onClick={handleSave} data-testid="tpl-save-btn" className="px-4 py-2 rounded-xl text-white text-[13px] font-medium flex items-center gap-2" style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #6366F1 60%, #DB2777 100%)' }}>
                <Save size={14} /> {editing._new ? 'Create' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewBody.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setPreviewBody({ open: false, html: '', subject: '' })} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-white/50 w-full max-w-2xl p-6">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-[15px] font-semibold text-slate-900">Preview (with sample data)</h3>
              <button onClick={() => setPreviewBody({ open: false, html: '', subject: '' })} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={16} /></button>
            </div>
            {previewBody.subject && <p className="text-[12.5px] text-slate-700 mb-2"><span className="font-medium">Subject:</span> {previewBody.subject}</p>}
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-[13px] text-slate-800 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: previewBody.html }} />
          </div>
        </div>
      )}
    </ServiceLayout>
  );
}

function TemplateRow({ tpl, onEdit, onDelete, onToggle, onPreview }) {
  return (
    <li className="p-4 hover:bg-slate-50/60 transition-colors" data-testid={`tpl-${tpl.id}`}>
      <div className="flex items-center gap-2">
        <span className="text-[13.5px] font-medium text-slate-900">{tpl.name}</span>
        <span className="text-[11px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">{tpl.trigger}</span>
        <span className="flex-1" />
        <button onClick={() => onToggle(tpl)} title={tpl.enabled ? 'Disable' : 'Enable'} className={`p-1 rounded transition-colors ${tpl.enabled ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-400 hover:bg-slate-100'}`}>
          {tpl.enabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
        </button>
        <button onClick={() => onPreview(tpl)} title="Preview" className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:text-sky-700 hover:border-sky-200 hover:bg-sky-50">
          <Eye size={13} />
        </button>
        <button onClick={() => onEdit(tpl)} title="Edit" className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:text-sky-700 hover:border-sky-200 hover:bg-sky-50">
          <Pencil size={13} />
        </button>
        <button onClick={() => onDelete(tpl)} title="Delete" className="p-1.5 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50">
          <Trash2 size={13} />
        </button>
      </div>
      {tpl.subject && (
        <p className="text-[12px] text-slate-600 mt-2"><span className="text-slate-400">Subject:</span> {tpl.subject}</p>
      )}
      <p className="text-[12px] text-slate-500 mt-1 line-clamp-2 leading-snug">{(tpl.body || '').replace(/<[^>]+>/g, '')}</p>
    </li>
  );
}
